/* -----------------Globals--------------- */
import { router, useLocalSearchParams } from "expo-router";

import { events } from "@constants/tracking";

import i18n from "@helpers/lib/i18n";
import useAnalytics from "@hooks/useAnalytics";
import OnboardingAppBar from "@memorang/ui/components/OnboardingLayout/OnboardingAppBar";
import ProgressSection from "@memorang/ui/components/OnboardingLayout/ProgressSection";
import { trackCustomEvent } from "analytics";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOnboardingContext } from "../contexts/OnboardingContext";
import OnboardingStepRenderer from "./OnboardingStepRenderer";
import FreeQuizSessionDialog from "./dialogs/FreeQuizSessionDialog";

const OnboardingRouteContainer = () => {
	const { step: stepString } = useLocalSearchParams<{
		step: string;
		selectedCategory: string;
	}>();

	const {
		stepsToShow = [],
		selectedCategoryId,
		categoryHasState,
		selectedStateCode,
		reset,
		handleUpdateProgress,
		stepProgress,
	} = useOnboardingContext();

	const { explicitCallTrackCustomEvent } = useAnalytics();

	const [showFreeQuizDialog, setShowFreeQuizDialog] = useState(false);

	const { t } = useTranslation();

	const currentStepData =
		stepsToShow.find((s) => s.step === stepString) || stepsToShow[0];

	useEffect(() => {
		if (currentStepData) {
			trackCustomEvent({
				eventName: events.onboardingStepStarted,
				step: currentStepData.step,
			});
		}
	}, [currentStepData]);

	const handleBack = () => {
		handleUpdateProgress?.("decrement");
		trackCustomEvent({
			eventName: events.onboardingStepNavPrev,
			step: currentStepData?.step,
			description: t(currentStepData?.descriptioni18n?.[i18n.language]!),
			title: t(currentStepData?.titlei18n?.[i18n.language]!),
		});

		switch (currentStepData?.step) {
			case "chooseCategory":
				router.setParams({
					step: "privacy",
				});
				break;
			case "chooseLevel":
				router.setParams({
					step: "chooseCategory",
				});
				break;
			case "chooseState":
				router.setParams({
					step: "chooseLevel",
				});
				break;
			case "chooseExam":
			case "chooseContext":
				router.setParams({
					step: selectedCategoryId
						? categoryHasState
							? selectedStateCode
								? "chooseState"
								: "chooseLevel"
							: "chooseCategory"
						: "privacy",
				});
				break;
			case "chooseDate":
			case "examDate":
				router.setParams({
					step: "chooseContext",
				});
				break;
			default:
				break;
		}
	};

	const isFirstStep = stepProgress ? stepProgress <= 0 : true;

	const handleNextAction = (stepName: string) => {
		explicitCallTrackCustomEvent({
			eventName: events.onboardingStepCompleted,
			step: currentStepData?.step,
		});
		explicitCallTrackCustomEvent({
			eventName: events.onboardingStepNavNext,
			step: currentStepData?.step,
		});
		handleUpdateProgress?.("increment");
		if (stepName !== "home") {
			router.setParams({
				step: stepName,
			});
		} else {
			router.replace("/home");
		}
	};

	return (
		<>
			<OnboardingAppBar
				title={
					currentStepData?.titlei18n?.[i18n.language] ||
					t(currentStepData?.title)
				}
				subtitle={
					currentStepData?.descriptioni18n?.[i18n.language] ||
					t(currentStepData?.description || "")
				}
				onBack={isFirstStep ? undefined : handleBack}
			/>
			{stepProgress != null && <ProgressSection progress={stepProgress} />}
			<OnboardingStepRenderer
				stepData={currentStepData ?? stepsToShow[0]}
				handleUpdateProgress={handleUpdateProgress!}
				cta={currentStepData?.ctai18n?.[i18n.language]}
				handleNextAction={handleNextAction}
				data={currentStepData?.data ?? []}
				reset={reset!}
			/>

			{showFreeQuizDialog && (
				<FreeQuizSessionDialog
					visible={showFreeQuizDialog}
					onClose={() => {
						setShowFreeQuizDialog(false);
						router.replace("/home");
					}}
				/>
			)}
		</>
	);
};

export default OnboardingRouteContainer;
